import React, { useState } from 'react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { AccountHelperInfo } from 'src/domains/players/webview/components/Account/component/accountHelperInfo/AccountHelperInfo';
import {
    AlignBottomWrapper,
    ForgotPasswordBtnWrapper,
    ForgotPasswordDescription,
    ForgotPasswordForm,
    ForgotPasswordWrapper,
} from './ForgotPasswordTab.style';
import { Input } from 'src/domains/players/webview/components/form/input/Input';
import {
    ForgotPasswordErrorMessageType,
    ForgotPasswordTabState,
} from 'src/domains/players/webview/components/Account/forgotPasswordTab/ForgotPasswordTab.state';
import { observer } from 'src/utils/mobx-react';
import { ResetPasswordSent } from 'src/domains/players/webview/components/Account/forgotPasswordTab/resetPasswordSent/ResetPasswordSent';
import { Messages } from 'src/domains/layouts/webview/components/Messages/Messages';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { UsersState } from 'src/domains/players/state/UsersState';

const renderMessage = (
    errorMessage: ForgotPasswordErrorMessageType,
    errorStatus: string | null,
    accountHelperMail: string
): JSX.Element | null => {
    if (errorMessage === null) {
        return null;
    }

    if (
        (errorMessage === 'suspended' || errorMessage === 'call-credit' || errorStatus === 'closed') &&
        errorStatus !== null
    ) {
        return (
            <Messages
                type='error'
                marginBottom='16px'
                message={
                    <I18n
                        langKey='account.reset-my-password.error-message'
                        defaultText='Your account has been {status} for security reasons. Please contact Customer Support at {accountHelperMail}'
                        params={{ status: errorStatus, accountHelperMail }}
                    />
                }
            />
        );
    }

    return (
        <Messages
            type='error'
            marginBottom='16px'
            message={errorMessage}
        />
    );
};

export const ForgotPasswordTab = observer('ForgotPasswordTab', () => {
    const common = useCommon();
    const configComponents = ConfigComponents.get(common).config;
    const languagesState = LanguagesState.get(common);
    const usersState = UsersState.get(common);

    const { forgotPasswordInputType: inputType, accountHelperMail } = configComponents;
    const [state] = useState(() => new ForgotPasswordTabState(accountHelperMail, usersState));

    if (state.showSuccessMessage === true) {
        return <ResetPasswordSent goBackToForgotPassword={state.goBackToForgotPassword} />;
    }

    return (
        <ForgotPasswordWrapper>
            <ForgotPasswordForm>
                <ForgotPasswordDescription data-test='forgot-password-description'>
                    <I18n
                        langKey='account.reset-my-password.form-description'
                        defaultText='Enter your email address and we will send you a link to reset your password. Reset link will only be sent if a valid account with the submitted email address was found'
                    />
                </ForgotPasswordDescription>

                <Input
                    colorTheme='light'
                    dataTest='forgot-password-email-input'
                    inputHasBackground={false}
                    label={languagesState.getTranslation('account.reset-my-password.email-label', 'Email')}
                    name='email'
                    placeholder={languagesState.getTranslation('account.reset-my-password.email-placeholder', 'Email')}
                    state={state.emailState}
                    type={inputType}
                />

                {renderMessage(state.errorMessage, state.errorStatus, accountHelperMail)}

                <ForgotPasswordBtnWrapper
                    dataTest='recover-password-button'
                    disabled={!state.isEmailValid}
                    isLoading={state.inProgress}
                    onClick={state.resetPassword}
                    size='large'
                >
                    <I18n
                        langKey='account.reset-my-password'
                        defaultText='Recover Password'
                    />
                </ForgotPasswordBtnWrapper>
            </ForgotPasswordForm>
            <AlignBottomWrapper>
                <AccountHelperInfo />
            </AlignBottomWrapper>
        </ForgotPasswordWrapper>
    );
});
